import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  getSceneList,
  saveScene,
  deleteScene,
  getHotspotList,
  saveHotspot,
  deleteHotspot,
  getUploadTempSignature
} from './api'

function extractPresignedUrl(signatureResponse) {
  const source = signatureResponse?.data || signatureResponse || {}
  const presignedUrl = source.url
  if (!presignedUrl) {
    throw new Error('上传凭证缺少 url 字段，请检查接口返回')
  }
  return presignedUrl
}

async function uploadLocalFileToCos(file) {
  const signatureResponse = await getUploadTempSignature(file.name)
  const presignedUrl = extractPresignedUrl(signatureResponse)
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream'
    },
    body: file
  })
  if (!uploadResponse.ok) {
    throw new Error('上传 COS 失败: ' + uploadResponse.status)
  }
  return presignedUrl.split('?')[0]
}

function unwrapList(res) {
  if (!res) return []
  const root = res.data !== undefined ? res.data : res
  if (Array.isArray(root)) return root
  if (root && Array.isArray(root.data)) return root.data
  if (root && Array.isArray(root.list)) return root.list
  return []
}

function parseJsonField(text, label) {
  const t = String(text || '').trim()
  if (!t) return undefined
  try {
    return JSON.parse(t)
  } catch (e) {
    throw new Error(label + ' 不是合法 JSON')
  }
}

function emptySceneForm() {
  return {
    id: null,
    name: '',
    imageUrl: '',
    thumbnailUrl: '',
    sortOrder: 0,
    northOffsetDeg: '',
    defaultConfigText: '',
    extraConfigText: '',
    state: 1
  }
}

function emptyHotspotForm() {
  return {
    id: null,
    hotspotType: 'link_scene',
    direction: 'straight',
    yawDeg: '',
    pitchDeg: '',
    label: '',
    icon: '',
    sortOrder: 0,
    zIndex: 0,
    visible: true,
    enabled: true,
    metadataText: '',
    targetSceneId: '',
    entryYawDeg: '',
    entryPitchDeg: '',
    transition: '',
    imageUrl: '',
    thumbUrl: '',
    title: '',
    caption: '',
    openMode: '',
    state: 1
  }
}

function parseHotspotMetadataValue(value) {
  if (value == null || value === '') return {}
  if (typeof value === 'object') return value
  try {
    const parsed = JSON.parse(String(value))
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

const HOTSPOT_DIRECTIONS = ['straight', 'left', 'right']

function inferDirectionFromMetadata(metadata) {
  const guide = metadata && metadata.guide
  if (!guide || guide.enabled === false) return 'straight'
  if (HOTSPOT_DIRECTIONS.includes(guide.type)) return guide.type
  const segments = Array.isArray(guide.segments) ? guide.segments : []
  if (segments.length <= 1) return 'straight'
  const start = Number(segments[0] && segments[0].yawDeg)
  const end = Number(segments[segments.length - 1] && segments[segments.length - 1].yawDeg)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 'right'
  let diff = end - start
  while (diff > 180) diff -= 360
  while (diff < -180) diff += 360
  return diff >= 0 ? 'right' : 'left'
}

function pickHotspotDirection(row) {
  const raw = row.direction != null ? String(row.direction).trim() : ''
  if (HOTSPOT_DIRECTIONS.includes(raw)) return raw
  return inferDirectionFromMetadata(
    parseHotspotMetadataValue(row.metadata != null ? row.metadata : row.meta)
  )
}

function formatDirectionLabel(direction) {
  if (direction === 'left') return '左转'
  if (direction === 'right') return '右转'
  if (direction === 'straight') return '直行'
  return '—'
}

export default defineComponent({
  name: 'VrEditView',
  setup() {
    const route = useRoute()
    const router = useRouter()

    const hallId = computed(() => {
      const id = route.params.hallId
      const n = parseInt(String(id), 10)
      return isFinite(n) ? n : null
    })

    const sceneOnlyActive = ref(true)
    const scenes = ref([])
    const sceneLoading = ref(false)
    const sceneMsg = ref('')
    const scenePanelOpen = ref(false)
    const sceneForm = ref(emptySceneForm())
    const panoramaFileRef = ref(null)
    const thumbFileRef = ref(null)
    const hotspotImageFileRef = ref(null)
    const hotspotThumbFileRef = ref(null)
    const uploadingPanorama = ref(false)
    const uploadingThumb = ref(false)
    const uploadingHotspotImage = ref(false)
    const uploadingHotspotThumb = ref(false)

    const hotspotOnlyActive = ref(true)
    const hotspotSceneId = ref(null)
    const hotspots = ref([])
    const hotspotLoading = ref(false)
    const hotspotMsg = ref('')
    const hotspotPanelOpen = ref(false)
    const hotspotForm = ref(emptyHotspotForm())

    const otherScenes = computed(() => {
      const sid = hotspotSceneId.value
      return scenes.value.filter(function (s) {
        const id = s.id
        return id != null && id !== sid
      })
    })

    const loadScenes = async () => {
      if (hallId.value == null) return
      sceneLoading.value = true
      sceneMsg.value = ''
      try {
        const res = await getSceneList({
          hall_id: hallId.value,
          only_active: sceneOnlyActive.value ? 1 : 0
        })
        scenes.value = unwrapList(res)
        if (
          hotspotSceneId.value != null &&
          !scenes.value.some(function (s) {
            return s.id === hotspotSceneId.value
          })
        ) {
          hotspotSceneId.value =
            scenes.value.length && scenes.value[0].id != null
              ? scenes.value[0].id
              : null
        }
        if (hotspotSceneId.value == null && scenes.value.length) {
          hotspotSceneId.value = scenes.value[0].id
        }
      } catch (e) {
        console.error(e)
        scenes.value = []
        sceneMsg.value = (e && e.message) || '加载场景失败'
      } finally {
        sceneLoading.value = false
      }
    }

    const loadHotspots = async () => {
      if (hallId.value == null || hotspotSceneId.value == null) {
        hotspots.value = []
        return
      }
      hotspotLoading.value = true
      hotspotMsg.value = ''
      try {
        const res = await getHotspotList({
          scene_id: hotspotSceneId.value,
          only_active: hotspotOnlyActive.value ? 1 : 0
        })
        hotspots.value = unwrapList(res)
      } catch (e) {
        console.error(e)
        hotspots.value = []
        hotspotMsg.value = (e && e.message) || '加载热点失败'
      } finally {
        hotspotLoading.value = false
      }
    }

    watch(hotspotSceneId, () => {
      loadHotspots()
    })

    watch(sceneOnlyActive, () => {
      loadScenes()
    })

    watch(hotspotOnlyActive, () => {
      loadHotspots()
    })

    watch(
      () => hotspotForm.value.hotspotType,
      (typ) => {
        if (typ === 'image') {
          hotspotForm.value = Object.assign({}, hotspotForm.value, { direction: '' })
        } else if (!HOTSPOT_DIRECTIONS.includes(String(hotspotForm.value.direction))) {
          hotspotForm.value = Object.assign({}, hotspotForm.value, { direction: 'straight' })
        }
      }
    )

    onMounted(() => {
      loadScenes()
    })

    const goBack = () => {
      router.push('/home')
    }

    const openNewScene = () => {
      sceneForm.value = emptySceneForm()
      scenePanelOpen.value = true
    }

    const openEditScene = (row) => {
      sceneForm.value = {
        id: row.id,
        name: row.name != null ? String(row.name) : '',
        imageUrl: row.imageUrl != null ? String(row.imageUrl) : '',
        thumbnailUrl:
          row.thumbnailUrl != null ? String(row.thumbnailUrl) : '',
        sortOrder:
          row.sortOrder != null && row.sortOrder !== ''
            ? Number(row.sortOrder)
            : 0,
        northOffsetDeg:
          row.northOffsetDeg != null && row.northOffsetDeg !== ''
            ? String(row.northOffsetDeg)
            : '',
        defaultConfigText:
          row.defaultConfig != null
            ? typeof row.defaultConfig === 'object'
              ? JSON.stringify(row.defaultConfig, null, 2)
              : String(row.defaultConfig)
            : '',
        extraConfigText:
          row.extraConfig != null
            ? typeof row.extraConfig === 'object'
              ? JSON.stringify(row.extraConfig, null, 2)
              : String(row.extraConfig)
            : '',
        state: row.state != null ? Number(row.state) : 1
      }
      scenePanelOpen.value = true
    }

    const cancelSceneForm = () => {
      scenePanelOpen.value = false
      sceneForm.value = emptySceneForm()
    }

    const submitScene = async () => {
      if (hallId.value == null) return
      const f = sceneForm.value
      if (!String(f.imageUrl || '').trim()) {
        alert('请先上传全景图')
        return
      }
      let defaultConfig
      let extraConfig
      try {
        defaultConfig = parseJsonField(f.defaultConfigText, 'defaultConfig')
        extraConfig = parseJsonField(f.extraConfigText, 'extraConfig')
      } catch (e) {
        alert(e.message)
        return
      }
      const body = {
        hall_id: hallId.value,
        imageUrl: String(f.imageUrl).trim()
      }
      if (String(f.name || '').trim()) body.name = String(f.name).trim()
      if (String(f.thumbnailUrl || '').trim())
        body.thumbnailUrl = String(f.thumbnailUrl).trim()
      body.sortOrder = Number(f.sortOrder) || 0
      if (f.northOffsetDeg !== '' && f.northOffsetDeg != null) {
        body.northOffsetDeg = Number(f.northOffsetDeg)
      }
      if (defaultConfig !== undefined) body.defaultConfig = defaultConfig
      if (extraConfig !== undefined) body.extraConfig = extraConfig
      if (f.state != null) body.state = Number(f.state)
      if (f.id != null) body.id = f.id
      sceneLoading.value = true
      sceneMsg.value = ''
      try {
        await saveScene(body)
        cancelSceneForm()
        await loadScenes()
      } catch (e) {
        console.error(e)
        alert((e && e.message) || '保存场景失败')
      } finally {
        sceneLoading.value = false
      }
    }

    const doDeleteScene = async (row) => {
      if (!confirm('确定软删除该场景？热点会级联删除。')) return
      if (hallId.value == null) return
      sceneLoading.value = true
      try {
        await deleteScene({ hall_id: hallId.value, id: row.id })
        if (hotspotSceneId.value === row.id) {
          hotspotSceneId.value = null
        }
        await loadScenes()
        await loadHotspots()
      } catch (e) {
        alert((e && e.message) || '删除失败')
      } finally {
        sceneLoading.value = false
      }
    }

    const openNewHotspot = () => {
      if (hotspotSceneId.value == null) {
        alert('请先选择场景')
        return
      }
      hotspotForm.value = emptyHotspotForm()
      hotspotPanelOpen.value = true
    }

    const openEditHotspot = (row) => {
      hotspotForm.value = {
        id: row.id,
        hotspotType:
          row.hotspotType === 'image' ? 'image' : 'link_scene',
        direction:
          row.hotspotType === 'image' ? '' : pickHotspotDirection(row),
        yawDeg:
          row.yawDeg != null && row.yawDeg !== '' ? String(row.yawDeg) : '',
        pitchDeg:
          row.pitchDeg != null && row.pitchDeg !== ''
            ? String(row.pitchDeg)
            : '',
        label: row.label != null ? String(row.label) : '',
        icon: row.icon != null ? String(row.icon) : '',
        sortOrder:
          row.sortOrder != null ? Number(row.sortOrder) : 0,
        zIndex: row.zIndex != null ? Number(row.zIndex) : 0,
        visible: row.visible !== false,
        enabled: row.enabled !== false,
        metadataText:
          row.metadata != null
            ? typeof row.metadata === 'object'
              ? JSON.stringify(row.metadata, null, 2)
              : String(row.metadata)
            : '',
        targetSceneId:
          row.targetSceneId != null ? String(row.targetSceneId) : '',
        entryYawDeg:
          row.entryYawDeg != null && row.entryYawDeg !== ''
            ? String(row.entryYawDeg)
            : '',
        entryPitchDeg:
          row.entryPitchDeg != null && row.entryPitchDeg !== ''
            ? String(row.entryPitchDeg)
            : '',
        transition:
          row.transition != null ? String(row.transition) : '',
        imageUrl: row.imageUrl != null ? String(row.imageUrl) : '',
        thumbUrl: row.thumbUrl != null ? String(row.thumbUrl) : '',
        title: row.title != null ? String(row.title) : '',
        caption: row.caption != null ? String(row.caption) : '',
        openMode: row.openMode != null ? String(row.openMode) : '',
        state: row.state != null ? Number(row.state) : 1
      }
      hotspotPanelOpen.value = true
    }

    const cancelHotspotForm = () => {
      hotspotPanelOpen.value = false
      hotspotForm.value = emptyHotspotForm()
    }

    const submitHotspot = async () => {
      if (hallId.value == null || hotspotSceneId.value == null) return
      const f = hotspotForm.value
      let metadata
      try {
        metadata = parseJsonField(f.metadataText, 'metadata')
      } catch (e) {
        alert(e.message)
        return
      }
      const body = {
        hall_id: hallId.value,
        scene_id: hotspotSceneId.value,
        hotspotType: f.hotspotType
      }
      if (f.yawDeg !== '') body.yawDeg = Number(f.yawDeg)
      if (f.pitchDeg !== '') body.pitchDeg = Number(f.pitchDeg)
      if (String(f.label || '').trim()) body.label = String(f.label).trim()
      if (String(f.icon || '').trim()) body.icon = String(f.icon).trim()
      body.sortOrder = Number(f.sortOrder) || 0
      body.zIndex = Number(f.zIndex) || 0
      body.visible = !!f.visible
      body.enabled = !!f.enabled
      body.state = f.state != null ? Number(f.state) : 1
      if (
        f.hotspotType === 'link_scene' ||
        (metadata !== undefined && Object.keys(metadata).length)
      ) {
        body.metadata = metadata
      }
      if (f.hotspotType === 'link_scene') {
        const dir = String(f.direction || '').trim()
        if (!HOTSPOT_DIRECTIONS.includes(dir)) {
          alert('link_scene 类型必须提供 direction（straight / left / right）')
          return
        }
        const tid = String(f.targetSceneId || '').trim()
        if (!tid) {
          alert('跳转场景需填写 targetSceneId')
          return
        }
        body.direction = dir
        body.targetSceneId = parseInt(tid, 10)
        if (f.entryYawDeg !== '') body.entryYawDeg = Number(f.entryYawDeg)
        if (f.entryPitchDeg !== '') body.entryPitchDeg = Number(f.entryPitchDeg)
        if (String(f.transition || '').trim())
          body.transition = String(f.transition).trim()
      } else {
        if (!f.id && !String(f.imageUrl || '').trim()) {
          alert('图片热点新建时需上传图片')
          return
        }
        if (String(f.imageUrl || '').trim()) body.imageUrl = String(f.imageUrl).trim()
        if (String(f.thumbUrl || '').trim()) body.thumbUrl = String(f.thumbUrl).trim()
        if (String(f.title || '').trim()) body.title = String(f.title).trim()
        if (String(f.caption || '').trim()) body.caption = String(f.caption).trim()
        if (String(f.openMode || '').trim()) body.openMode = String(f.openMode).trim()
      }
      if (f.id != null) body.id = f.id

      hotspotLoading.value = true
      try {
        await saveHotspot(body)
        cancelHotspotForm()
        await loadHotspots()
      } catch (e) {
        alert((e && e.message) || '保存热点失败')
      } finally {
        hotspotLoading.value = false
      }
    }

    const doDeleteHotspot = async (row) => {
      if (!confirm('确定软删除该热点？')) return
      if (hallId.value == null || hotspotSceneId.value == null) return
      hotspotLoading.value = true
      try {
        await deleteHotspot({
          hall_id: hallId.value,
          scene_id: hotspotSceneId.value,
          id: row.id
        })
        await loadHotspots()
      } catch (e) {
        alert((e && e.message) || '删除失败')
      } finally {
        hotspotLoading.value = false
      }
    }

    const truncate = (s, n) => {
      if (s == null) return '—'
      const t = String(s)
      return t.length > n ? t.slice(0, n) + '…' : t
    }

    const triggerPanoramaPick = () => {
      const el = panoramaFileRef.value
      if (el) {
        el.value = ''
        el.click()
      }
    }

    const triggerThumbPick = () => {
      const el = thumbFileRef.value
      if (el) {
        el.value = ''
        el.click()
      }
    }

    const handlePanoramaFile = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      uploadingPanorama.value = true
      try {
        const url = await uploadLocalFileToCos(file)
        sceneForm.value = Object.assign({}, sceneForm.value, { imageUrl: url })
      } catch (e) {
        console.error(e)
        alert((e && e.message) || '全景图上传失败')
      } finally {
        uploadingPanorama.value = false
      }
    }

    const handleThumbFile = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      uploadingThumb.value = true
      try {
        const url = await uploadLocalFileToCos(file)
        sceneForm.value = Object.assign({}, sceneForm.value, {
          thumbnailUrl: url
        })
      } catch (e) {
        console.error(e)
        alert((e && e.message) || '缩略图上传失败')
      } finally {
        uploadingThumb.value = false
      }
    }

    const triggerHotspotImagePick = () => {
      const el = hotspotImageFileRef.value
      if (el) {
        el.value = ''
        el.click()
      }
    }

    const triggerHotspotThumbPick = () => {
      const el = hotspotThumbFileRef.value
      if (el) {
        el.value = ''
        el.click()
      }
    }

    const handleHotspotImageFile = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!file.type || !file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      uploadingHotspotImage.value = true
      try {
        const url = await uploadLocalFileToCos(file)
        hotspotForm.value = Object.assign({}, hotspotForm.value, { imageUrl: url })
      } catch (e) {
        console.error(e)
        alert((e && e.message) || '热点图片上传失败')
      } finally {
        uploadingHotspotImage.value = false
      }
    }

    const handleHotspotThumbFile = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!file.type || !file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      uploadingHotspotThumb.value = true
      try {
        const url = await uploadLocalFileToCos(file)
        hotspotForm.value = Object.assign({}, hotspotForm.value, { thumbUrl: url })
      } catch (e) {
        console.error(e)
        alert((e && e.message) || '缩略图上传失败')
      } finally {
        uploadingHotspotThumb.value = false
      }
    }

    const openPanoramaPreview = () => {
      if (hallId.value == null) return
      var origin = typeof window !== 'undefined' ? window.location.origin : ''
      var apiRaw =
        typeof process !== 'undefined' &&
        process.env &&
        process.env.VUE_APP_BASE_API
          ? String(process.env.VUE_APP_BASE_API).trim()
          : ''
      var apiNorm =
        apiRaw !== ''
          ? apiRaw.replace(/\/$/, '')
          : (function () {
              var host = window.location.hostname
              if (host === 'localhost' || host === '127.0.0.1') {
                return window.location.protocol + '//' + host + ':8000'
              }
              return window.location.origin + '/api'
            })()
      var params = new URLSearchParams()
      params.set('hall_id', String(hallId.value))
      if (hotspotSceneId.value != null) {
        params.set('scene_id', String(hotspotSceneId.value))
      }
      var tok = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''
      if (tok) {
        params.set('token', tok)
      }
      params.set('api_base', apiNorm)
      var url = origin + '/panorama.html?' + params.toString()
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    return {
      hallId,
      goBack,
      openPanoramaPreview,
      sceneOnlyActive,
      scenes,
      sceneLoading,
      sceneMsg,
      scenePanelOpen,
      sceneForm,
      panoramaFileRef,
      thumbFileRef,
      uploadingPanorama,
      uploadingThumb,
      triggerPanoramaPick,
      triggerThumbPick,
      handlePanoramaFile,
      handleThumbFile,
      hotspotImageFileRef,
      hotspotThumbFileRef,
      uploadingHotspotImage,
      uploadingHotspotThumb,
      triggerHotspotImagePick,
      triggerHotspotThumbPick,
      handleHotspotImageFile,
      handleHotspotThumbFile,
      openNewScene,
      openEditScene,
      cancelSceneForm,
      submitScene,
      doDeleteScene,
      hotspotOnlyActive,
      hotspotSceneId,
      hotspots,
      hotspotLoading,
      hotspotMsg,
      hotspotPanelOpen,
      hotspotForm,
      otherScenes,
      openNewHotspot,
      openEditHotspot,
      cancelHotspotForm,
      submitHotspot,
      doDeleteHotspot,
      loadScenes,
      loadHotspots,
      truncate,
      formatDirectionLabel,
      resolveHotspotDirection: (row) => {
        if (!row || row.hotspotType === 'image') return ''
        return pickHotspotDirection(row)
      }
    }
  }
})
